import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { onboardingKeys } from '@/api/keys/onboardingKeys';
import type { OnboardingState } from '@/api/queries/useOnboardingQueries';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

interface CompleteTourResponse {
  message: string;
  tourVersionSeen: string;
}

/**
 * Hook to mark the onboarding tour as complete
 */
export function useCompleteTour() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch<CompleteTourResponse>('/onboarding/complete/', {
        method: 'POST',
      }),
    onSuccess: () => {
      // Track onboarding completed
      analytics.capture(AnalyticsEvents.ONBOARDING_COMPLETED, {
        steps_completed: 'tour',
      });

      // Synchronous cache stamp — same race class as the step mutations above.
      // The backend sets tourVersionSeen = currentTourVersion, which makes
      // needsTour = false and isReturningUser = false (hasTakenInitialQuiz && needsTour).
      // Stamping synchronously prevents any future guard-redirect that reads
      // needsTour: true from a stale cache mid-transition.
      queryClient.setQueryData<OnboardingState>(
        onboardingKeys.status(),
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            tourVersionSeen: prev.currentTourVersion,
            needsTour: false,
            isReturningUser: false,
          };
        }
      );
      // Fire-and-forget: reconcile cache with server after the optimistic stamp.
      queryClient.invalidateQueries({ queryKey: onboardingKeys.status() });
    },
  });
}

/**
 * Hook to mark the permissions onboarding step as complete
 */
export function useCompletePermissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch<{ message: string }>('/onboarding/complete-permissions/', {
        method: 'POST',
      }),
    onSuccess: () => {
      // Synchronously stamp the cache so OnboardingGuard does not read stale
      // needsPermissions: true on the very next navigation. invalidateQueries is
      // async and does not update the cache synchronously — see the race-condition
      // pattern documented in paire-wiki/Frontend/Onboarding Guard Pattern.md.
      queryClient.setQueryData<OnboardingState>(
        onboardingKeys.status(),
        (prev) => {
          if (!prev) return prev;
          return { ...prev, permissionsCompleted: true, needsPermissions: false };
        }
      );
      // Fire-and-forget: reconcile cache with server after the optimistic stamp.
      queryClient.invalidateQueries({ queryKey: onboardingKeys.status() });
    },
  });
}

/**
 * Hook to mark the find friends onboarding step as complete
 */
export function useCompleteFriends() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch<{ message: string }>('/onboarding/complete-friends/', {
        method: 'POST',
      }),
    onSuccess: () => {
      // Synchronous cache stamp — see useCompletePermissions comment above.
      queryClient.setQueryData<OnboardingState>(
        onboardingKeys.status(),
        (prev) => {
          if (!prev) return prev;
          return { ...prev, friendsCompleted: true, needsFriends: false };
        }
      );
      queryClient.invalidateQueries({ queryKey: onboardingKeys.status() });
    },
  });
}

/**
 * Hook to mark the profile setup onboarding step as complete
 */
export function useCompleteProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch<{ message: string }>('/onboarding/complete-profile/', {
        method: 'POST',
      }),
    onSuccess: () => {
      // Synchronous cache stamp — see useCompletePermissions comment above.
      queryClient.setQueryData<OnboardingState>(
        onboardingKeys.status(),
        (prev) => {
          if (!prev) return prev;
          return { ...prev, profileCompleted: true, needsProfile: false };
        }
      );
      queryClient.invalidateQueries({ queryKey: onboardingKeys.status() });
    },
  });
}
